/**
 * CustomerSummaryTab - Aba de resumo do modal premium de cliente
 * Exibe métricas e atividades recentes de forma simplificada
 * 
 * @version 3.1.0
 * RELEASE: 2026-01-10 - Usa TimelineContainer para consistência visual da barra de rolagem
 */
import React from 'react';
import { 
  Briefcase, 
  Trophy, 
  DollarSign, 
  TrendingUp,
  Clock
} from 'lucide-react';
import { CrmCustomer } from '@/hooks/useCrmCustomers';
import { CustomerModalData } from '@/hooks/useCustomerModalData';
import { PriceDisplay } from '@/components/crm/PriceDisplay';
import { CustomerActivityTimelinePremium } from '../components/CustomerActivityTimelinePremium';
import { CustomerInlineActivityCreator } from '../components/CustomerInlineActivityCreator';

import { TimelineContainer } from '@/components/crm/TimelineContainer';
import { cn } from '@/lib/utils';

// Props do componente
interface CustomerSummaryTabProps {
  customer: CrmCustomer;
  modalData: CustomerModalData | null;
  isActive?: boolean; // Para lazy loading das atividades
  /**
   * v3.4.0: Modo Contato (contato ainda sem cadastro de cliente).
   * Oculta métricas dependentes do cliente e usa a timeline por contact_id —
   * os andamentos permanecem os mesmos quando o contato virar cliente.
   */
  isContactMode?: boolean;
  /** v3.4.0: ID do contato usado no modo Contato */
  contactId?: string | null;
  /**
   * v3.5.0 (mobile): conteúdo da sidebar injetado no topo do Resumo quando o
   * modal está em tela cheia no celular (mesmo padrão do DealSummaryTab).
   */
  sidebarContent?: React.ReactNode;
}

/**
 * Card de métrica compacto
 */
function MetricCard({ 
  icon: Icon, 
  label, 
  value, 
  subValue,
  variant = 'default' 
}: { 
  icon: typeof Briefcase; 
  label: string; 
  value: React.ReactNode;
  subValue?: string;
  variant?: 'default' | 'primary' | 'success' | 'warning';
}) {
  const variantClasses = {
    default: 'bg-muted/30',
    primary: 'bg-primary/5',
    success: 'bg-emerald-50 dark:bg-emerald-900/20',
    warning: 'bg-amber-50 dark:bg-amber-900/20',
  };

  return (
    <div className={cn('rounded-lg p-3 space-y-1', variantClasses[variant])}>
      <div className="flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-muted-foreground" />
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
      </div>
      <div className="text-lg font-bold leading-none">{value}</div>
      {subValue && (
        <span className="text-[10px] text-muted-foreground">{subValue}</span>
      )}
    </div>
  );
}

/**
 * Aba de resumo do cliente (simplificada)
 * Exibe apenas métricas e atividades recentes
 */
export function CustomerSummaryTab({
  customer,
  modalData,
  isActive = true,
  isContactMode = false,
  contactId = null,
  sidebarContent,
}: CustomerSummaryTabProps) {
  // Dados das métricas
  const counters = modalData?.counters || { total_deals: 0, won_deals: 0, open_deals: 0, conversations: 0 };
  const metrics = modalData?.metrics || { total_value: 0, win_rate: 0 };

  return (
    /**
     * v3.2.0: Removido TimelineContainer (ScrollArea + pr-3) — scroll agora é
     * controlado pelo wrapper do modal (paridade com DealSummaryTab), evitando
     * padding-right extra e melhor aproveitamento do espaço horizontal.
     */
    <div className="space-y-4">
      {/* v3.5.0: ficha lateral no mobile (injetada pelo modal) */}
      {sidebarContent && <div className="-mx-1">{sidebarContent}</div>}

      {/* Grid de métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard 
          icon={Briefcase} 
          label="Total Negócios" 
          value={counters.total_deals}
          subValue={`${counters.open_deals} em aberto`}
        />
        <MetricCard 
          icon={Trophy} 
          label="Negócios Ganhos" 
          value={counters.won_deals}
          variant="success"
        />
        <MetricCard 
          icon={DollarSign} 
          label="Valor Total" 
          value={<PriceDisplay amount={metrics.total_value} currency="BRL" size="sm" />}
          variant="primary"
        />
        <MetricCard 
          icon={TrendingUp} 
          label="Taxa de Conversão" 
          value={`${metrics.win_rate}%`}
          variant={metrics.win_rate >= 50 ? 'success' : 'warning'}
        />
      </div>

      {/* Atividades Recentes (sem card externo, mesmo padrão do modal de negócio) */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center">
            <Clock className="h-3 w-3 text-muted-foreground" />
          </div>
          <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Atividades Recentes
          </h3>
        </div>
        {/* v3.3.0: criador inline de andamentos (mesmo padrão do modal de negócios) */}
        <div className="pb-3">
          <CustomerInlineActivityCreator
            customerId={isContactMode ? null : customer.id}
            contactId={contactId}
          />
        </div>
        <div className="pb-4">
          <CustomerActivityTimelinePremium 
            customerId={isContactMode ? null : customer.id}
            contactId={contactId}
            paginated
            isActive={isActive} 
          />
        </div>

      </div>
    </div>
  );
}
